// @flow

import React from 'react';
import styled from 'styled-components';
import { Button } from '@blueprintjs/core';
import CategorizedPill from './categorized-pill';
import CategorizedPickerPopover from './categorized-picker-popover';
import type { CategoryMeta, Option } from './categorized-picker-popover';

export type Filter = {
  id: string,
  categoryKey: string,
  valueLabel: string,
  color: string,
};

type Props = {
  label: string,
  categories: $ReadOnlyArray<CategoryMeta>,
  options: $ReadOnlyArray<Option>,
  activeFilters: $ReadOnlyArray<Filter>,
  onAdd: (option: Option) => mixed,
  onRemove: (filter: Filter) => mixed,
  onClear: () => mixed,
  emptyHelperText?: string,
  categoryHeaderLabel: string,
};

const Bar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 0;
  border-bottom: 1px solid #ebf1f5;
  margin-bottom: 12px;
`;

const Label = styled.span`
  font-size: 12px;
  color: #5c7080;
  margin-right: 4px;
`;

const Helper = styled.span`
  font-size: 12px;
  color: #5c7080;
  margin-left: auto;
`;

const findCategory = (
  categories: $ReadOnlyArray<CategoryMeta>,
  key: string,
): ?CategoryMeta => categories.find((c) => c.key === key);

const buildHelper = (
  activeFilters: $ReadOnlyArray<Filter>,
  categories: $ReadOnlyArray<CategoryMeta>,
  emptyHelperText: string,
): string => {
  if (activeFilters.length === 0) return emptyHelperText;
  if (activeFilters.length <= 4) {
    return `Filtering by ${activeFilters
      .map((f) => {
        const cat = findCategory(categories, f.categoryKey);
        const label = cat ? cat.label : f.categoryKey;
        return `${label}: ${f.valueLabel}`;
      })
      .join(', ')}`;
  }
  const counts: { [string]: number } = {};
  activeFilters.forEach((f) => {
    counts[f.categoryKey] = (counts[f.categoryKey] || 0) + 1;
  });
  const parts = Object.keys(counts).map((k) => {
    const cat = findCategory(categories, k);
    const label = cat ? cat.label : k;
    return `${counts[k]} ${label.toLowerCase()}${counts[k] > 1 ? 's' : ''}`;
  });
  return `Matching ${parts.join(' + ')}`;
};

const CategorizedFilterBar = ({
  label,
  categories,
  options,
  activeFilters,
  onAdd,
  onRemove,
  onClear,
  emptyHelperText = 'No filters applied',
  categoryHeaderLabel,
}: Props) => {
  const helper = buildHelper(activeFilters, categories, emptyHelperText);
  const excludeIds = activeFilters.map((f) => f.id);

  return (
    <Bar>
      <Label>{label}</Label>
      {activeFilters.map((f) => {
        const cat = findCategory(categories, f.categoryKey);
        const typeLabel = cat ? cat.label : f.categoryKey;
        return (
          <CategorizedPill
            key={f.id}
            typeLabel={typeLabel}
            valueLabel={f.valueLabel}
            color={f.color}
            size="md"
            onRemove={() => onRemove(f)}
          />
        );
      })}
      <CategorizedPickerPopover
        categories={categories}
        options={options}
        excludeIds={excludeIds}
        singleSelectCategories={[]}
        size="md"
        addLabel={activeFilters.length === 0 ? 'Add filter' : 'Add another'}
        categoryHeaderLabel={categoryHeaderLabel}
        onPick={onAdd}
      />
      {activeFilters.length >= 2 && (
        <Button minimal small onClick={onClear} text="Clear all" />
      )}
      <Helper>{helper}</Helper>
    </Bar>
  );
};

export default CategorizedFilterBar;
